import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import '../fleet/fleet.css'
import { dismissAjaxError } from '../../state/actions/app'
import { DrivingLogsRouter } from './drivingLog/drivingLogsRouter'
import BatteryReportPage from './batteryReport/batteryReportPage'
import WeightReportPage from './weightReport/weightReportPage'
import TollPassagePage from './tollPassages/tollPassagePage'
import TemperatureReportPage from './temperatureReport/temperatureReportPage'
import HumidityReportPage from './humidityReport/humidityReportPage'
import { isHumiditySensorBroken } from '@modul-connect/shared/config'
import AssetTrackerReportPage from './assetTrackerReport-debug/assetTrackerReportPage'
import ReportDashboardPage from './reportsDashboard'
import MainPage from '../mainPage'
import { useParams } from 'react-router-dom'
import { getServiceList } from '../../utils/ServiceList'
import { ServiceType } from '@modul-connect/shared/utils/services'
import LpsReportPage from './lpsReport/lpsReportPage'
import Checklist_demo from './demo/checklist_demo'
import { isAllowedToSeeDemoPages } from '../../utils/app'
import SecuritySystemReportPage from './securitySystem/securitySystemReportPage'
import AssetsReportPage from './assetTrackerStatus/assetsReportPage'
import LockReportPage from './lockReports/lockReportPage'
import { ClimateSystemPage } from './temperatureReport/climateSystemPage'
import { useTranslation } from 'react-i18next'

const hideAssetTrackerPage = true // remove this once Modul gave their OK. For now, they want these reports accessible via direct URL, but hide the link from users.

const drivingReportsId = 'driving-logs'
const batteryReportsId = 'battery-reports'
const weightReportsId = 'weight-reports'
const tollReportsId = 'toll-reports'
const temperatureReportsId = 'temperature-reports'
const humidityReportsId = 'humidity-reports'
const assetTrackersDebugId = 'asset-tracker-reports'
const securityIncidentRepotsId = 'security-incident'
const locksRepotsId = 'locks-reports'

const lithiumPowerSupplyId = 'lps-reports'
const assetTrackerReportsId = 'asset-trackers'

export const octopusDemoChecklist = 'checklist-demo'

const Reports = ({ 
  subtrees,
  authenticated_user
}) => {
  const { option } = useParams()
  const {t} = useTranslation()

  let blockScrolling = false

  const title = option?.includes('_') && option.split('_').length > 1 ? option.split('_')[1] : undefined

  const services = getServiceList({ subtrees })
  const hasDrivingLogAccess = services.hasService(ServiceType.DrivingLogs)
  const hasBatteryAccess = services.hasService(ServiceType.BatteryWarnings)
  const hasWeightAccess = services.hasService(ServiceType.WeightReports)
  const hasTollAccess = services.hasService(ServiceType.TollPassages)
  const hasClimateAccess = services.hasService(ServiceType.ClimateReports)
  const hasAssetTrackersAccess = services.hasService(ServiceType.AssetTrackers)
  const hasLpsReports = services.hasService(ServiceType.LpsReports)
  const hasSecurityIncidentReports = services.hasService(ServiceType.SecuritySystemReports)
  const hasLockReports = services.hasService(ServiceType.LocksReports)

  const noMapPage = services.hasService(ServiceType.mapAccessNoGeo)

  const organisationsWithAssetTrackerService = services.organisationsHasService(ServiceType.AssetTrackers)

  const organisationsWithPrivilege = organisationsWithAssetTrackerService.filter(org => {

    const userTag = authenticated_user?.tags?.find(t => t?.tags === org?.uuid)

    return userTag && userTag.privilege !== 'standard'
  })

  const organisationNodes = organisationsWithPrivilege ? organisationsWithPrivilege.map(org => ({
    type: 'item',
    id: org.uuid,
    title: org.name,
    indent: true
  })) : []

  const assetTrackerNodes = [
    { type: 'item', id: 'trackers', title: 'Trackers' },
    { type: 'item', id: 'maintenance', title: 'Maintenance' },
  ]

  if (organisationNodes.length > 0) {
    assetTrackerNodes.push({ type: 'divider' })
    assetTrackerNodes.push({ type: 'subtitle', text: 'Settings', indent: true })
    assetTrackerNodes.push(...organisationNodes)
  }

  const allReports = [
    {
      id: 'dashboard',
      title: 'Dashboard',
      content: <ReportDashboardPage />,
      icon: 'Dashboard',
    },
    {
      id: drivingReportsId,
      title: t('drivingJournal.drivingReports'),
      content: <DrivingLogsRouter />,
      icon: 'DJBlack',
      subPages: [
        { id: 'main', title: t('drivingJournal.drivingReports') },
        { id: 'idle-time', title: t('drivingJournal.idleTimeReport.title') },
      ],
    },
    {
      id: batteryReportsId,
      title: 'Battery Reports',
      content: <BatteryReportPage />,
      icon: 'BMBlack',
      subPages: [{ id: 'main', title: 'Battery Reports' }],
    },
    {
      id: weightReportsId,
      title: 'Weight Reports',
      content: <WeightReportPage />,
      icon: 'VWBlack',
      subPages: [{ id: 'main', title: 'Weight Reports' }],
    },
    {
      id: tollReportsId,
      title: 'Toll Reports',
      content: <TollPassagePage />,
      icon: 'TRBlack',
      subPages: [{ id: 'main', title: 'Toll Reports' }],
    },
    {
      id: temperatureReportsId,
      title: t('climateSystem.sidebar.climateSystem'),
      content: <ClimateSystemPage />,
      icon: 'Temp',
      subPages: [{ id: 'temp-history', title: t('climateSystem.sidebar.temperatureHistory') }, { id: 'temp-logs', title: t('climateSystem.sidebar.temperatureLogs') }, { id: 'settings', title: t('climateSystem.sidebar.settings') }],
    },
    {
      id: humidityReportsId,
      title: 'Humidity Reports',
      hide: isHumiditySensorBroken,
      content: <HumidityReportPage />,
      icon: 'Hum',
      subPages: [{ id: 'main', title: 'Humidity Reports' }],
    },
    {
      id: assetTrackerReportsId,
      title: 'Asset Trackers',
      content: <AssetsReportPage />,
      icon: 'AssetTracker',
      nodes: assetTrackerNodes
    },
    {
      id: assetTrackersDebugId,
      title: 'Asset Tracker Reports',
      hide: hideAssetTrackerPage,
      content: <AssetTrackerReportPage />,
      icon: 'AssetTracker',
      subPages: [{ id: 'main', title: 'Asset Trackers' }],
    },
    {
      id: lithiumPowerSupplyId,
      title: 'LPS',
      content: <LpsReportPage />,
      icon: 'LPS',
      subPages: [{ id: 'history', title: 'History' },{ id: 'footprint', title: 'Footprint' }]
    },
    {
      id: securityIncidentRepotsId,
      title: 'Security System',
      content: <SecuritySystemReportPage />,
      icon: 'SecuritySystem',
      subPages: [{ id: 'incidents', title: 'Incidents' }]
    },
    {
      id: locksRepotsId,
      title: 'Locks Reports',
      content: <LockReportPage />,
      icon: 'LockIcon',
      subPages: [{ id: 'main', title: 'Lock reports' }]
    }
  ]
  
  const availableServices = noMapPage ? allReports : allReports.filter(r => r.id !== 'dashboard')

  const isAllowedToSeeDemo = isAllowedToSeeDemoPages()
  if (isAllowedToSeeDemo) availableServices.push({
    id: octopusDemoChecklist,
    title: 'Checklist',
    content: <Checklist_demo />,
    icon: 'Checklist',
    subPages: [{ id: 'reports', title: 'Checklist reports' }, { id: 'defects', title: 'Defect reports' }, { id: 'settings', title: 'Settings' }]
  })

  const sidebarContent = availableServices.length
    ? availableServices
    : [{ title: 'You have no active services', content: null, icon: null }]

  const [wordReplacements, setWordReplacements] = useState(null)

  const hideBreadcrumbs = true //!selected || (selected?.length < 2) || sidebarContent[selected]?.id === 'dashboard'

  switch (title) { 
    case drivingReportsId: {
      if (!hasDrivingLogAccess) blockScrolling = true
      break
    }
    case batteryReportsId: {
      if (!hasBatteryAccess) blockScrolling = true
      break
    }
    case weightReportsId: {
      if (!hasWeightAccess) blockScrolling = true
      break
    }
    case tollReportsId: {
      if (!hasTollAccess) blockScrolling = true
      break
    }
    case temperatureReportsId: {
      if (!hasClimateAccess) blockScrolling = true
      break
    }
    case humidityReportsId: {
      if (!hasClimateAccess) blockScrolling = true
      break
    }
    case assetTrackerReportsId: {
      if (!hasAssetTrackersAccess) blockScrolling = true
      break
    }
    case assetTrackersDebugId: {
      if (!hasAssetTrackersAccess) blockScrolling = true
      break
    }
    case lithiumPowerSupplyId: {
      if (!hasLpsReports) blockScrolling = true
      break
    }
    case securityIncidentRepotsId:{
      if(!hasSecurityIncidentReports) blockScrolling = true
      break
    }
    case locksRepotsId:{
      if(!hasLockReports) blockScrolling = true
      break
    }
    default: break
  }

  return (
    <MainPage 
      rootURL={'reports'}
      landingPageURL={'/reports/dashboard'}
      initialPageIndex={0} // the initial value needs to match the value of the landing page (see routes file)
      hideBreadcrumbs={hideBreadcrumbs}
      breadCrumbWordReplacements={wordReplacements}
      sidebarContent={sidebarContent}
      blockScrolling={blockScrolling}
    />
  )
}

const mapStateToProps = ({ 
  subtrees,
  authenticated_user
}) => ({
  subtrees,
  authenticated_user
})

const mapDispatchToProps = dispatch => ({
  dismissAjaxError: () => dispatch(dismissAjaxError()),
})

export default connect(mapStateToProps, mapDispatchToProps)(Reports)